import { ReactNode, useState } from 'react';
import SvgIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';

import Card from 'src/components/streametric/card/Card';
import { useResponsive } from 'src/hooks/useResponsive';

import FiltersDrawer from './FiltersDrawer';
import DesktopFilters from './DesktopFilters';
import MobileFilters from './MobileFilters';
import { FilterItem, RemoveFilter } from './types';

type Props = {
  /** Filters shown in the drawer. */
  filters: FilterItem[];
  /** Filters rendered as chips below the header row. */
  appliedFilters: FilterItem[];
  onApply: (filters: FilterItem[]) => void;
  removeFilter: RemoveFilter;
  onClearAll: () => void;
  isFormsLoading?: boolean;
  /** Rendered on the left of the header row, e.g. pagination. */
  leading?: ReactNode;
};

export const FiltersBar = ({
  filters,
  appliedFilters,
  onApply,
  removeFilter,
  onClearAll,
  isFormsLoading = false,
  leading,
}: Props) => {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);

  return (
    <>
      <FiltersDrawer
        onClose={() => {
          setOpenFiltersDrawer(false);
        }}
        isFormsLoading={isFormsLoading}
        open={openFiltersDrawer}
        filters={filters}
        updateFilters={(updatedFilters) => {
          onApply(updatedFilters);
          setOpenFiltersDrawer(false);
        }}
      />
      <Stack>
        <Card
          sx={{
            flexShrink: 0,
            color: theme.palette.grey[500],
            marginInline: '0',
            maxWidth: '100%',
            width: '100%',
            position: 'relative',
            paddingLeft: '5px',
          }}
          cardContentSx={{ padding: '4px 12px 4px 12px !important' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: '16px' }}>
            {/* Applied filters: left side of the row; on mobile they wrap below it. */}
            <Stack
              flexDirection="row"
              alignItems="center"
              flexWrap="wrap"
              gap="10px"
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                order: isMobile ? 1 : 0,
                flexBasis: isMobile ? '100%' : 0,
                paddingBlock: appliedFilters.length && isMobile ? '4px' : 0,
              }}
            >
              {appliedFilters?.length > 0 ? (
                <>
                  <Typography sx={{ color: theme.palette.grey[800] }} variant="body1">
                    Filters
                  </Typography>
                  {appliedFilters.map((filter) =>
                    filter.renderChip ? (
                      filter.renderChip(filter, { removeFilter })
                    ) : (
                      <Chip
                        size="small"
                        color="primary"
                        key={filter.name}
                        label={filter.label}
                        onDelete={() => {
                          removeFilter(filter.name);
                        }}
                      />
                    )
                  )}
                  <Button
                    onClick={onClearAll}
                    sx={{ flexShrink: 0, color: theme.palette.grey[600] }}
                    variant="text"
                    size="small"
                  >
                    <Stack gap="4px" alignItems="center" flexDirection="row">
                      <SvgIcon fontSize="small" component={DeleteIcon} />
                      <Typography variant="caption">Clear all</Typography>
                    </Stack>
                  </Button>
                </>
              ) : null}
            </Stack>

            <Box
              sx={{
                flex: isMobile ? 1 : '0 0 auto',
                minWidth: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                // MUI's pagination toolbar has a 52px min-height, which pushes its content below
                // the centre of the row next to the Filters button.
                '.MuiTablePagination-root': { height: 'auto' },
                '.MuiTablePagination-toolbar': { minHeight: 'auto' },
              }}
            >
              {leading}
            </Box>
            <Box sx={{ flexShrink: 0, width: isMobile ? '56px' : 'auto' }}>
              {isMobile ? (
                <MobileFilters
                  openFilters={() => {
                    setOpenFiltersDrawer(true);
                  }}
                />
              ) : (
                <DesktopFilters
                  openFilters={() => {
                    setOpenFiltersDrawer(true);
                  }}
                />
              )}
            </Box>
          </Box>
        </Card>
      </Stack>
    </>
  );
};
